// ===================================================================
// PA GRANTS — Cross-Project EHP Compliance
// ===================================================================

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Stack, Badge, Button, TextInput, Select,
  Modal, ActionIcon, Loader, Center, SimpleGrid,
} from '../../components/gab/GabComponents';
import { IconPlus, IconEdit, IconTrash, IconSearch, IconRefresh, IconLeaf } from '@tabler/icons-react';
import { usePAGrantsStore } from '../../store/paGrantsStore';
import { notifications } from '../../lib/notifications';

const STATUS_OPTIONS = [
  { value: '', label: 'Not Started' },
  { value: 'not_required', label: 'Not Required' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'complete', label: 'Complete' },
  { value: 'cleared', label: 'Cleared' },
  { value: 'required', label: 'Required' },
];

const STATUS_COLORS: Record<string, string> = {
  not_required: '#939598', in_progress: '#C68700', complete: 'var(--color-success)', cleared: '#03AC9E', required: '#F86A0B',
};

const FLOOD_OPTIONS = [
  { value: '', label: 'N/A' },
  { value: 'required', label: 'Required' },
  { value: 'obtained', label: 'Obtained' },
  { value: 'waived', label: 'Waived' },
];

const REVIEW_OPTIONS = [
  { value: '', label: 'N/A' },
  { value: 'not_required', label: 'Not Required' },
  { value: 'required', label: 'Required' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'complete', label: 'Complete' },
];

const EMPTY_FORM = {
  nepaRequired: false, nepaStatus: '',
  section106Required: false, section106Status: '',
  floodplainReview: '', floodInsurance: '',
  hazmatReview: '', esaReview: '', wetlandsReview: '',
  notes: '',
};

function statusBadge(val: string) {
  if (!val) return <Badge size="xs" variant="light" color="#939598">Not Started</Badge>;
  return <Badge size="xs" variant="light" color={STATUS_COLORS[val] || '#939598'}>{val.replace(/_/g, ' ')}</Badge>;
}

function overallStatus(r: any): { label: string; color: string } {
  const statuses = [r.nepaStatus, r.section106Status, r.floodplainReview, r.esaReview];
  if (statuses.every(s => !s || s === 'not_required' || s === 'complete' || s === 'cleared'))
    return { label: 'Cleared', color: 'var(--color-success)' };
  if (statuses.some(s => s === 'in_progress' || s === 'required'))
    return { label: 'In Progress', color: '#C68700' };
  return { label: 'Not Started', color: '#939598' };
}

export default function Compliance() {
  const {
    compliance, fetchCompliance, createCompliance, updateCompliance, deleteCompliance,
    projects, fetchProjects,
  } = usePAGrantsStore();

  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []); // eslint-disable-line

  async function load() {
    setLoading(true);
    await Promise.all([fetchCompliance(), fetchProjects()]);
    setLoading(false);
  }

  const projectMap = useMemo(() => {
    const m: Record<number, string> = {};
    projects.forEach((p: any) => { m[p.Rid] = p.projectNumber || p.title || `Project ${p.Rid}`; });
    return m;
  }, [projects]);

  const filtered = useMemo(() => {
    let list = compliance as any[];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(r =>
        (r.notes || '').toLowerCase().includes(q) ||
        (r.nepaStatus || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [compliance, search]);

  function openAdd() {
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
    setModalOpen(true);
  }

  function openEdit(r: any) {
    setEditingId(r.Rid);
    setForm({
      nepaRequired: r.nepaRequired || false, nepaStatus: r.nepaStatus || '',
      section106Required: r.section106Required || false, section106Status: r.section106Status || '',
      floodplainReview: r.floodplainReview || '', floodInsurance: r.floodInsurance || '',
      hazmatReview: r.hazmatReview || '', esaReview: r.esaReview || '',
      wetlandsReview: r.wetlandsReview || '', notes: r.notes || '',
    });
    setModalOpen(true);
  }

  async function handleSave() {
    setSaving(true);
    try {
      if (editingId) {
        await updateCompliance(editingId, form);
      } else {
        await createCompliance(form);
      }
      notifications.show({ message: editingId ? 'Updated' : 'Added', color: 'var(--color-success)' });
      setModalOpen(false);
    } catch {
      notifications.show({ message: 'Failed to save', color: 'var(--color-error)' });
    }
    setSaving(false);
  }

  async function handleDelete(r: any) {
    if (!confirm('Delete this compliance record?')) return;
    await deleteCompliance(r.Rid);
    notifications.show({ message: 'Deleted', color: '#939598' });
  }

  const cleared = filtered.filter((r: any) => overallStatus(r).color === 'var(--color-success)').length;
  const inProgress = filtered.filter((r: any) => overallStatus(r).color === '#C68700').length;

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={3} c="black">EHP Compliance</Title>
        <Button variant="primary" size="sm" leftIcon={<IconPlus size={14} />} onClick={openAdd}>
          Add Record
        </Button>
      </Group>

      {filtered.length > 0 && (
        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
          {[
            { label: 'Total Records', value: filtered.length.toString() },
            { label: 'Cleared', value: cleared.toString(), color: cleared > 0 ? 'var(--color-success)' : undefined },
            { label: 'In Progress', value: inProgress.toString(), color: inProgress > 0 ? '#F86A0B' : undefined },
          ].map(s => (
            <Paper key={s.label} p="sm" radius="md" withBorder>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{s.label}</Text>
              <Text size="lg" fw={700} c={s.color}>{s.value}</Text>
            </Paper>
          ))}
        </SimpleGrid>
      )}

      <Paper p="md" radius="md" withBorder>
        <Group gap="sm" mb="md">
          <TextInput
            placeholder="Search…"
            leftSection={<IconSearch size={14} />}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex: 1 }}
            size="sm"
          />
          <ActionIcon variant="subtle" size="md" onClick={load} title="Refresh">
            <IconRefresh size={16} />
          </ActionIcon>
        </Group>

        {loading ? (
          <Center py="xl"><Loader /></Center>
        ) : filtered.length === 0 ? (
          <Stack align="center" py="xl" gap="sm">
            <IconLeaf size={40} color="var(--color-neutral-400)" />
            <Text c="dimmed">No EHP compliance records found.</Text>
            <Button size="sm" variant="light" leftIcon={<IconPlus size={14} />} onClick={openAdd}>
              Add First Record
            </Button>
          </Stack>
        ) : (
          <Stack gap="sm">
            {filtered.map((r: any) => {
              const overall = overallStatus(r);
              return (
                <Paper key={r.Rid} p="md" withBorder>
                  <Group justify="space-between" mb="sm">
                    <Group gap="sm">
                      {r.projectId && (
                        <Text fw={600}>{projectMap[r.projectId] || `Project #${r.projectId}`}</Text>
                      )}
                      <Badge variant="light" color={overall.color}>{overall.label}</Badge>
                    </Group>
                    <Group gap="xs">
                      <ActionIcon variant="subtle" size="sm" onClick={() => openEdit(r)}>
                        <IconEdit size={14} />
                      </ActionIcon>
                      <ActionIcon variant="subtle" size="sm" color="var(--color-error)" onClick={() => handleDelete(r)}>
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Group>
                  </Group>

                  <SimpleGrid cols={{ base: 2, md: 4 }} spacing="xs">
                    <Stack gap={2}>
                      <Text size="xs" c="dimmed" tt="uppercase">NEPA</Text>
                      {statusBadge(r.nepaStatus)}
                    </Stack>
                    <Stack gap={2}>
                      <Text size="xs" c="dimmed" tt="uppercase">Section 106</Text>
                      {statusBadge(r.section106Status)}
                    </Stack>
                    <Stack gap={2}>
                      <Text size="xs" c="dimmed" tt="uppercase">Floodplain</Text>
                      {statusBadge(r.floodplainReview)}
                    </Stack>
                    <Stack gap={2}>
                      <Text size="xs" c="dimmed" tt="uppercase">ESA Review</Text>
                      {statusBadge(r.esaReview)}
                    </Stack>
                    <Stack gap={2}>
                      <Text size="xs" c="dimmed" tt="uppercase">Hazmat</Text>
                      {statusBadge(r.hazmatReview)}
                    </Stack>
                    <Stack gap={2}>
                      <Text size="xs" c="dimmed" tt="uppercase">Wetlands</Text>
                      {statusBadge(r.wetlandsReview)}
                    </Stack>
                    <Stack gap={2}>
                      <Text size="xs" c="dimmed" tt="uppercase">Flood Insurance</Text>
                      {statusBadge(r.floodInsurance)}
                    </Stack>
                  </SimpleGrid>

                  {r.notes && <Text size="xs" c="dimmed" mt="sm">{r.notes}</Text>}
                </Paper>
              );
            })}
          </Stack>
        )}
      </Paper>

      <Modal opened={modalOpen} onClose={() => setModalOpen(false)} title={editingId ? 'Edit Compliance Record' : 'Add Compliance Record'} size="lg">
        <Stack gap="sm">
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <Select label="NEPA Status" data={STATUS_OPTIONS} value={form.nepaStatus} onChange={v => setForm(f => ({ ...f, nepaStatus: v || '' }))} />
            <Select label="Section 106 Status" data={STATUS_OPTIONS} value={form.section106Status} onChange={v => setForm(f => ({ ...f, section106Status: v || '' }))} />
            <Select label="Floodplain Review" data={REVIEW_OPTIONS} value={form.floodplainReview} onChange={v => setForm(f => ({ ...f, floodplainReview: v || '' }))} />
            <Select label="Flood Insurance" data={FLOOD_OPTIONS} value={form.floodInsurance} onChange={v => setForm(f => ({ ...f, floodInsurance: v || '' }))} />
            <Select label="ESA Review" data={REVIEW_OPTIONS} value={form.esaReview} onChange={v => setForm(f => ({ ...f, esaReview: v || '' }))} />
            <Select label="Hazmat Review" data={REVIEW_OPTIONS} value={form.hazmatReview} onChange={v => setForm(f => ({ ...f, hazmatReview: v || '' }))} />
            <Select label="Wetlands Review" data={REVIEW_OPTIONS} value={form.wetlandsReview} onChange={v => setForm(f => ({ ...f, wetlandsReview: v || '' }))} />
          </SimpleGrid>
          <TextInput label="Notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          <Group justify="flex-end" gap="sm" mt="sm">
            <Button variant="ghost" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button variant="primary" loading={saving} onClick={handleSave}>Save</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
